import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  candidateSignup,
  requestPasswordReset,
  sendWelcomeEmail,
  updateMyProfile,
  updateMyShowcase,
  uploadFile,
} from "@/lib/candidate-api";
import { slugFor } from "@/components/site/showcase";
import { salesRoles, salespersonCountries } from "@/components/site/roles";
import ProfileFormView from "@/components/cl/views/ProfileFormView";

export const Route = createFileRoute("/signup")({
  // Set by /apply/$jobId's "Create an account" link, so a brand-new candidate
  // lands back on that role and finishes applying.
  validateSearch: (
    search: Record<string, unknown>,
  ): { jobId?: string; invite?: string; inv?: string } => ({
    ...(typeof search["jobId"] === "string" && search["jobId"]
      ? { jobId: search["jobId"] as string }
      : {}),
    ...(typeof search["invite"] === "string" && search["invite"]
      ? { invite: search["invite"] as string }
      : {}),
    ...(typeof search["inv"] === "string" && search["inv"] ? { inv: search["inv"] as string } : {}),
  }),
  head: () => {
    const t = "Create your profile | CLOSDD";
    const d =
      "Create your free CLOSDD profile in seven short steps, record your 60-second pitch and get heard by employers hiring salespeople.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: Signup,
});

const base =
  "min-height:44px;padding:0 18px;border-radius:999px;font:inherit;font-size:15px;font-weight:600;cursor:pointer;";
const CHIP_ON = "background:#0B2160;color:#FFFFFF;border:2px solid #0B2160";
const CHIP_OFF = "background:#FFFFFF;color:#0B2160;border:2px solid #BDBDB5";
const ROLE_FROM_TEASER: Record<string, string> = {
  "Sales executive": "Sales executive",
  "Business development": "Business development manager",
  "Account management": "Account manager",
  Recruitment: "Recruitment consultant",
  Fundraising: "Fundraiser",
  "Appointment setting": "Appointment setter",
  Closing: "High-Ticket Closer",
  "Sales management": "Sales manager",
};
// Signup only takes the pitch; the five assessment answers are recorded from My profile.
const SLOTS = ["pitch"] as const;
type Slot = (typeof SLOTS)[number];
const MAX_SECONDS = 60;

/** Explains a failed getUserMedia() in terms a candidate can act on, shown right under the Record button. */
function micErrorText(err: unknown): string {
  const name = err instanceof DOMException ? err.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Your browser is blocking the microphone. Click the icon left of closdd.com in the address bar, set Microphone to Allow, then press Record again. On a Mac, also check System Settings → Privacy & Security → Microphone.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "We couldn't find a microphone. Plug one in or check your sound settings, then press Record again.";
  }
  if (name === "NotReadableError" || name === "AbortError") {
    return "Your microphone couldn't start. Close any other app using it (Zoom, Teams, FaceTime), then press Record again.";
  }
  return "We couldn't start recording. Allow microphone access and press Record again.";
}

/** File extension matching what MediaRecorder actually produced (Safari records mp4, not webm). */
const extFor = (mime: string) => (/mp4|m4a|aac/i.test(mime) ? "m4a" : /ogg/i.test(mime) ? "ogg" : "webm");

const randomPassword = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return (
    Array.from(bytes, (b) => b.toString(36).padStart(2, "0"))
      .join("")
      .slice(0, 24) + "Aa1!"
  );
};

function Signup() {
  const navigate = useNavigate();
  const { jobId, invite, inv } = Route.useSearch();
  const [on, setOn] = useState<Record<string, boolean>>({});
  const [recorded, setRecorded] = useState<Record<string, boolean>>({});
  const [recording, setRecording] = useState<Slot | "">("");
  const [submitted, setSubmitted] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [micNote, setMicNote] = useState<{ text: string; error: boolean } | null>(null);
  const takes = useRef<Partial<Record<Slot, { blob: Blob; seconds: number }>>>({});
  const recorder = useRef<{
    mr: MediaRecorder;
    stream: MediaStream;
    started: number;
    timer: number;
  } | null>(null);

  // Details typed into the "create your profile" teaser carry over.
  useEffect(() => {
    let d: Record<string, unknown> | null = null;
    try {
      d = JSON.parse(sessionStorage.getItem("closdd-signup") ?? "null");
    } catch {
      /* ignore */
    }
    if (!d) return;
    const parts = String(d["name"] ?? "")
      .split(/\s+/)
      .filter(Boolean);
    const loc = String(d["location"] ?? "");
    const carriedRoles = Array.isArray(d["roles"])
      ? d["roles"].filter((role): role is string => typeof role === "string")
      : [];
    const legacyRole = String(d["role"] ?? "");
    const roles = (
      carriedRoles.length
        ? carriedRoles
        : legacyRole
          ? [ROLE_FROM_TEASER[legacyRole] ?? legacyRole]
          : []
    ).filter((role) => salesRoles.includes(role as (typeof salesRoles)[number]));
    const values: Record<string, string> = {
      "pf-first": parts[0] ?? "",
      "pf-last": parts.slice(1).join(" "),
      "pf-email": String(d["email"] ?? ""),
      "pf-phone": String(d["phone"] ?? ""),
      "pf-loc": /^\s*remote\s*$/i.test(loc)
        ? ""
        : loc.replace(/,?\s*or remote\s*$/i, "").replace(/,?\s*remote\s*$/i, ""),
      "pf-country": String(d["country"] ?? ""),
      "pf-years": String(d["years"] ?? ""),
    };
    for (const [id, val] of Object.entries(values)) {
      const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
      if (el && val && !el.value) el.value = val;
    }
    setOn((o) => ({
      ...o,
      ...(roles.length ? Object.fromEntries(roles.map((role) => [`role:${role}`, true])) : {}),
      ...(/remote/i.test(loc) ? { "mode:Remote": true } : {}),
    }));
    setPrefilled(true);
  }, []);

  useEffect(() => () => recorder.current?.stream.getTracks().forEach((t) => t.stop()), []);

  const stop = () => {
    const r = recorder.current;
    if (r && r.mr.state !== "inactive") r.mr.stop();
  };

  const toggleRecord = async (slot: Slot) => {
    if (recording === slot) return stop();
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setMicNote({ text: "This browser can't record audio. Open closdd.com in the latest Chrome, Safari or Edge to record your pitch.", error: true });
      return;
    }
    // The permission prompt can sit unnoticed in the address bar — say so
    // while we wait, instead of the button looking like it did nothing.
    setMicNote({ text: "Allow microphone access when your browser asks.", error: false });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicNote(null);
      const mr = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      const started = Date.now();
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        window.clearTimeout(recorder.current?.timer);
        stream.getTracks().forEach((t) => t.stop());
        takes.current[slot] = {
          blob: new Blob(chunks, { type: mr.mimeType || "audio/webm" }),
          seconds: Math.min(MAX_SECONDS, Math.round((Date.now() - started) / 1000)),
        };
        setRecorded((r) => ({ ...r, [slot]: true }));
        setRecording("");
      };
      recorder.current = {
        mr,
        stream,
        started,
        timer: window.setTimeout(() => mr.state !== "inactive" && mr.stop(), MAX_SECONDS * 1000),
      };
      mr.start();
      setRecording(slot);
    } catch (err) {
      setMicNote({ text: micErrorText(err), error: true });
    }
  };

  const chip = (group: string, label: string) => {
    const k = `${group}:${label}`;
    return {
      label,
      pressed: on[k] ? "true" : "false",
      pick: () =>
        setOn((o) => {
          if (o[k]) return { ...o, [k]: false };
          if (group === "role" || group === "skill") {
            const selectedCount = Object.keys(o).filter(
              (key) => o[key] && key.startsWith(`${group}:`),
            ).length;
            if (selectedCount >= 3) return o;
          }
          return { ...o, [k]: true };
        }),
      chipStyle: base + (on[k] ? CHIP_ON : CHIP_OFF),
    };
  };
  const slot = (k: Slot) => ({
    on: !!recorded[k] && recording !== k,
    off: !recorded[k] || recording === k,
    label: recording === k ? "Stop recording" : "Record answer",
    toggle: () => void toggleRecord(k),
  });

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const form = e.currentTarget;
    const el = (id: string) => form.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
    const val = (id: string) => (el(id)?.value ?? "").trim();
    const picked = (g: string) =>
      Object.keys(on)
        .filter((k) => on[k] && k.startsWith(`${g}:`))
        .map((k) => k.slice(g.length + 1));
    const roles = picked("role");
    if (!roles.length) {
      setError("Choose at least one role you want.");
      return;
    }

    setError("");
    setBusy(true);
    let accountCreated = false;
    try {
      const name = `${val("pf-first")} ${val("pf-last")}`.trim();
      const email = val("pf-email");
      // The form has no password field, so the account gets a random one and
      // the candidate sets their own from the reset email straight after.
      await candidateSignup(name, email, randomPassword());
      accountCreated = true;
      void requestPasswordReset(email).catch(() => undefined);

      // CV, track record, About and the sales assessment are added from My
      // profile after login — signup is details, role and the pitch only.
      const take = takes.current.pitch;
      const pitch = take
        ? { url: await uploadFile(take.blob, `pitch.${extFor(take.blob.type)}`), seconds: take.seconds }
        : null;

      const modes = picked("mode");

      await updateMyProfile({
        name,
        email,
        phone: val("pf-phone"),
        location: val("pf-loc"),
        role: roles.join(", "),
        years: val("pf-years"),
        hasPitch: Boolean(pitch),
      });
      await updateMyShowcase({
        slug: slugFor(name, email),
        headline: val("pf-head"),
        targetRole: roles.join(", "),
        location: [val("pf-loc"), val("pf-country")].filter(Boolean).join(", "),
        availability: val("pf-avail"),
        yearsSelling: val("pf-years"),
        skillTags: picked("skill"),
        locationPref: modes[0] === "Office based" ? "On-site" : (modes[0] ?? ""),
        ...(pitch ? { audioUrl: pitch.url, audioSeconds: pitch.seconds } : {}),
      });

      try {
        sessionStorage.removeItem("closdd-signup");
      } catch {
        /* ignore */
      }
      if (jobId) {
        await navigate({
          to: "/apply/$jobId",
          params: { jobId },
          search: { invite: invite ?? "", ...(inv ? { inv } : {}) },
        });
        return;
      }
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't create your profile. Try again.");
    } finally {
      setBusy(false);
      // After the profile and pitch are saved (or a later step failed), so the
      // email shows what's actually done. The API only ever sends it once.
      if (accountCreated) void sendWelcomeEmail().catch(() => undefined);
    }
  }

  const rec = Object.fromEntries(SLOTS.map((k) => [k, slot(k)]));
  const note =
    error ||
    (busy
      ? "Creating your profile…"
      : submitted
        ? "We've emailed you a link to set your password."
        : "");

  return (
    <>
      <ProfileFormView
        v={{
          workModes: ["Remote", "Hybrid", "Office based"].map((x) => chip("mode", x)),
          countries: salespersonCountries,
          roles: salesRoles.map((x) => chip("role", x)),
          skills: [
            "Sales",
            "Lead generation",
            "Appointment setting",
            "Business development",
            "Account management",
            "Closing",
            "Cold calling",
            "Recruitment",
            "Fundraising",
            "Customer success",
            "Sales management",
          ].map((x) => chip("skill", x)),
          rec,
          micNote,
          submitted,
          prefilled,
          editing: !submitted,
          submit,
          edit: () => setSubmitted(false),
        }}
      />
      {note ? (
        <p
          role="alert"
          style={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#FFFFFF",
            color: error ? "#B3261E" : "#0B2160",
            border: "1px solid #E3E3DE",
            borderRadius: 8,
            padding: "12px 18px",
            fontFamily: "Archivo, system-ui, sans-serif",
            fontWeight: 700,
            zIndex: 50,
            maxWidth: "90vw",
          }}
        >
          {note}
        </p>
      ) : null}
    </>
  );
}
